import { Controller } from '@hotwired/stimulus';

// Enables a step's Next button only while every required field has a value.
//
// No Ruby component class — like form_component, this is behavior attached to
// a plain form/container rendered by the consuming view.
//
// `required: true` on a UI Kit component is the single declaration: simple
// controls render a native `required` attribute on their input, and composite
// widgets (location field, search input, date picker, document uploader) mark
// their root with `data-required` because their values live in hidden inputs,
// which browsers don't validate. This controller reads both — views never
// re-declare which fields gate the button.
//
// The gated button is the `next` target (a real <button>, enabled/disabled in
// place). Steps that navigate instead of submitting set
// `data-step-form-component-next-url-value` and point the button's click at
// `visitNext`.
//
// When attached to a <form>, submitting disables the button so a slow request
// can't be submitted twice.
//
// Re-evaluation happens on native input/change plus the UI Kit controllers'
// change events, which all bubble.

const KIT_CHANGE_EVENTS = [
  'select-field-component:change',
  'location-field-component:change',
  'location-field-component:reset',
  'date-picker-component:change',
  'selected-chips-component:added',
  'selected-chips-component:removed',
  'phone-number-field-component:change',
  'selection-button-group-component:change'
];

export default class extends Controller {
  static targets = ['next'];

  static values = { nextUrl: String };

  connect() {
    this.evaluate = this.evaluate.bind(this);
    this.lock = this.lock.bind(this);
    this.watchedEvents = ['input', 'change', ...KIT_CHANGE_EVENTS];
    this.watchedEvents.forEach(name => this.element.addEventListener(name, this.evaluate));
    this.element.addEventListener('submit', this.lock);
    this.evaluate();
  }

  disconnect() {
    this.watchedEvents.forEach(name => this.element.removeEventListener(name, this.evaluate));
    this.element.removeEventListener('submit', this.lock);
    clearTimeout(this.evaluateTimer);
  }

  visitNext(event) {
    event.preventDefault();
    if (!this.satisfied() || !this.nextUrlValue) return;

    window.location.assign(this.nextUrlValue);
  }

  // The submit has already been dispatched; disabling now only prevents a
  // second click while the request is in flight.
  lock() {
    this.nextTargets.forEach(button => { button.disabled = true; });
  }

  // Deferred a tick: composite widgets sync their hidden inputs in a Stimulus
  // value callback, a microtask AFTER dispatching their change event, so
  // reading the DOM synchronously would see the previous selection.
  evaluate() {
    clearTimeout(this.evaluateTimer);
    this.evaluateTimer = setTimeout(() => {
      const ready = this.satisfied();
      this.nextTargets.forEach(button => { button.disabled = !ready; });
    }, 0);
  }

  satisfied() {
    return this.nativeRequiredFields().every(field => this.fieldSatisfied(field)) &&
      this.requiredComposites().every(root => this.compositeSatisfied(root));
  }

  nativeRequiredFields() {
    return Array.from(
      this.element.querySelectorAll('input[required], textarea[required], select[required]')
    );
  }

  requiredComposites() {
    return Array.from(this.element.querySelectorAll('[data-required]'));
  }

  // A composite widget submits through named fields (hidden inputs, a radio
  // group): one carrying a value is enough. Disabled fields don't submit
  // (e.g. a selection group's unselected options), so they don't count.
  compositeSatisfied(root) {
    const fields = Array.from(root.querySelectorAll('[name]'));
    return fields.some(field => !field.disabled && field.name !== '' && this.fieldSatisfied(field));
  }

  fieldSatisfied(field) {
    if (field.type === 'checkbox' || field.type === 'radio') {
      return field.checked && field.value.trim() !== '';
    }

    return (field.value || '').trim() !== '';
  }
}
